import { useState, useEffect, lazy, Suspense } from "react";
import { trackEvent } from './utils/analytics.js';
import { C, DEFAULT_ZONES, DEFAULT_MATERIALS, ALL_SVCS, SVC_MAP } from "./data/index.js";
import { getMarket } from "./data/markets.js";
import { calcPrice } from "./utils/pricing.js";
import { parseScopeFallback } from "./utils/scopeParser.js";
import { generatePDF } from "./utils/pdfEstimate.js";
import { generatePandL } from "./utils/pdfPandL.js";
import { useJsPDF } from "./hooks/useJsPDF.js";
import { useVoiceDictation } from "./hooks/useVoiceDictation.js";

import { Header } from "./components/Header.jsx";
import { BottomTabs } from "./components/BottomTabs.jsx";

// Lazy-loaded screens for code splitting
const SignupScreen = lazy(() => import("./components/screens/SignupScreen.jsx").then(m => ({ default: m.SignupScreen })));
const OnboardingScreen = lazy(() => import("./components/screens/OnboardingScreen.jsx").then(m => ({ default: m.OnboardingScreen })));
const DashboardScreen = lazy(() => import("./components/screens/DashboardScreen.jsx").then(m => ({ default: m.DashboardScreen })));
const Step1Screen = lazy(() => import("./components/screens/Step1Screen.jsx").then(m => ({ default: m.Step1Screen })));
const Step2Screen = lazy(() => import("./components/screens/Step2Screen.jsx").then(m => ({ default: m.Step2Screen })));
const AnalyzeScreen = lazy(() => import("./components/screens/AnalyzeScreen.jsx").then(m => ({ default: m.AnalyzeScreen })));
const ReviewScreen = lazy(() => import("./components/screens/ReviewScreen.jsx").then(m => ({ default: m.ReviewScreen })));
const TierSelectScreen = lazy(() => import("./components/screens/TierSelectScreen.jsx").then(m => ({ default: m.TierSelectScreen })));
const PreviewScreen = lazy(() => import("./components/screens/PreviewScreen.jsx").then(m => ({ default: m.PreviewScreen })));
const PricingScreen = lazy(() => import("./components/screens/PricingScreen.jsx").then(m => ({ default: m.PricingScreen })));
const CostsScreen = lazy(() => import("./components/screens/CostsScreen.jsx").then(m => ({ default: m.CostsScreen })));
const ProfileScreen = lazy(() => import("./components/screens/ProfileScreen.jsx").then(m => ({ default: m.ProfileScreen })));
const SettingsScreen = lazy(() => import("./components/screens/SettingsScreen.jsx").then(m => ({ default: m.SettingsScreen })));
const BuyScreen = lazy(() => import("./components/screens/BuyScreen.jsx").then(m => ({ default: m.BuyScreen })));
const ClientsScreen = lazy(() => import("./components/screens/ClientsScreen.jsx").then(m => ({ default: m.ClientsScreen })));
const ClientDetailScreen = lazy(() => import("./components/screens/ClientDetailScreen.jsx").then(m => ({ default: m.ClientDetailScreen })));
const AttachClientScreen = lazy(() => import("./components/screens/AttachClientScreen.jsx").then(m => ({ default: m.AttachClientScreen })));
const ReportDetailScreen = lazy(() => import("./components/screens/ReportDetailScreen.jsx").then(m => ({ default: m.ReportDetailScreen })));

// Modals loaded eagerly (small, needed on multiple screens)
import { PriceEditModal } from "./components/modals/PriceEditModal.jsx";
import { ClientEditModal } from "./components/modals/ClientEditModal.jsx";
import { AttachClientModal } from "./components/modals/AttachClientModal.jsx";

// Suspense fallback for lazy screens
const ScreenLoader = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
    <div className="spin-gold" />
  </div>
);

export default function App() {
  const [screen, setScreen] = useState("signup");
  const [dir, setDir] = useState("forward");
  const [signStep, setSignStep] = useState(1);
  const [email, setEmail] = useState("");
  const [biz, setBiz] = useState("");
  const [phone, setPhone] = useState("");

  // Subscription state (replaces old credit system)
  const [subscription, setSubscription] = useState({ plan: null, status: "trial", trialEnds: null }); // plan: "pro"|"premium", status: "trial"|"active"|"expired"|"paused"
  const hasAccess = subscription.status === "trial" || subscription.status === "active";
  const [reportHistory, setReportHistory] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);

  const [address, setAddress] = useState(null);
  const [propertyData, setPropertyData] = useState(null);
  const [measuring, setMeasuring] = useState(false);

  const [zones, setZones] = useState(DEFAULT_ZONES.map(z => ({ ...z, photos: [] })));
  const [newZoneLabel, setNewZoneLabel] = useState("");

  const [desc, setDesc] = useState("");
  const [svcs, setSvcs] = useState(["maint"]);
  const [cond, setCond] = useState("norm");
  const [aiSvcs, setAiSvcs] = useState([]);
  const [aiCond, setAiCond] = useState(null);
  const [flags, setFlags] = useState([]);
  const [detects, setDetects] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [usedFallback, setUsedFallback] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loadMsg, setLoadMsg] = useState("");

  const [selectedTier, setSelectedTier] = useState("pro");
  const [selectedClientForReport, setSelectedClientForReport] = useState(null);

  const [profile, setProfile] = useState({
    servicesOffered: ["maint", "hedge", "shrub", "leaves", "bed", "mulch", "pine", "fert", "weed", "firstcut", "spring", "fallclean"],
    teamMembers: [
      { id: "owner", name: "You", payType: "salary", rate: 0, active: true },
    ],
    materials: DEFAULT_MATERIALS.map(m => ({ ...m })),
    expenses: [
      { id: "insurance", label: "Insurance", monthlyAmount: 0 },
      { id: "vehicle", label: "Vehicle/fuel", perJobAmount: 0 },
      { id: "office", label: "Office/overhead", monthlyAmount: 0 },
    ],
    estimatedMonthlyJobs: 40,
    customExpenses: [],
    customPrices: {},
    minMarginPct: 40,
    minJobPrice: 45,
    standardFees: [
      { id: "dump", label: "Dump fee", price: 0, perJob: false },
      { id: "trip", label: "Trip charge", price: 0, perJob: true },
      { id: "fuel", label: "Fuel surcharge", price: 0, perJob: true },
      { id: "minvisit", label: "Minimum visit fee", price: 0, perJob: true },
    ],
    customFees: [],
  });
  const [activeTab, setActiveTab] = useState("reports");

  const [editingPrice, setEditingPrice] = useState(null);
  const [editPriceValue, setEditPriceValue] = useState("");

  const [logo, setLogo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUnlocked, setLogoUnlocked] = useState(false);

  const [scopeSlider, setScopeSlider] = useState(50);
  const [recurringSlider, setRecurringSlider] = useState(50);

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [editingClient, setEditingClient] = useState(null);
  const [attachClientTo, setAttachClientTo] = useState(null);

  const { ready: pdfReady, error: pdfError, retry: pdfRetry } = useJsPDF();
  const voice = useVoiceDictation(setDesc);

  const go = (next, d = "forward") => { setDir(d); setScreen(next); };
  const back = (to) => go(to, "back");

  const infoOk = email.includes("@") && biz.trim().length >= 2;
  const step1Ok = address && propertyData && !measuring;
  const hasPhotosOrDesc = zones.some(z => z.photos.length > 0) || desc.trim().length > 0;
  const step2Ok = step1Ok && hasPhotosOrDesc;

  // Client helpers
  const getClientStatus = (client) => {
    const clientReports = reportHistory.filter(r => r.clientId === client.id);
    if (clientReports.length === 0) return "lead";
    const hasRecurring = clientReports.some(r => r.services?.includes("maint"));
    const lastReport = clientReports[0];
    const daysSince = lastReport?.fullDate ? Math.floor((Date.now() - new Date(lastReport.fullDate).getTime()) / 86400000) : 999;
    if (daysSince > 60) return "inactive";
    if (hasRecurring) return "active";
    return "onetime";
  };

  const addClient = (clientData) => {
    const newClient = {
      id: "cl_" + Date.now(),
      name: clientData.name || "",
      phone: clientData.phone || "",
      email: clientData.email || "",
      addresses: clientData.address ? [clientData.address] : [],
      notes: clientData.notes || "",
      createdAt: new Date().toISOString(),
    };
    setClients(p => [newClient, ...p]);
    return newClient;
  };

  const updateClient = (id, updates) => {
    setClients(p => p.map(c => c.id === id ? { ...c, ...updates } : c));
  };

  const attachReportToClient = (reportId, clientId) => {
    setReportHistory(p => p.map(r => r.id === reportId ? { ...r, clientId } : r));
  };

  const filteredClients = clientSearch.trim()
    ? clients.filter(c =>
        c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
        c.email.toLowerCase().includes(clientSearch.toLowerCase()) ||
        c.phone.includes(clientSearch) ||
        c.addresses.some(a => a.toLowerCase().includes(clientSearch.toLowerCase()))
      )
    : clients;

  // Photo zone handlers
  const addPhotoToZone = (zoneId, photo) => {
    setZones(prev => prev.map(z => z.id === zoneId ? { ...z, photos: [...z.photos, photo].slice(0, 5) } : z));
  };
  const removePhotoFromZone = (zoneId, idx) => {
    setZones(prev => prev.map(z => z.id === zoneId ? { ...z, photos: z.photos.filter((_, i) => i !== idx) } : z));
  };
  const addCustomZone = () => {
    if (!newZoneLabel.trim()) return;
    const id = "custom_" + Date.now();
    setZones(prev => [...prev, { id, label: newZoneLabel.trim(), photos: [] }]);
    setNewZoneLabel("");
  };

  // Live pricing recalc
  useEffect(() => {
    if (!address || !propertyData) return;
    setPricing(calcPrice(propertyData.grassSqFt, svcs, cond, address.lat, address.lng, profile));
  }, [svcs, cond, address, propertyData, profile]);

  // Address → measure property (via Worker proxy, with timeout fallback)
  const handleAddressSelect = async (place) => {
    setAddress(place); setPropertyData(null); setMeasuring(true);
    // Clear stale photos from previous address
    setZones(DEFAULT_ZONES.map(z => ({ ...z, photos: [] })));

    const fallback = { grassSqFt: 4600, totalSqFt: 8200, houseSqFt: 1700, treesCount: 3 };

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      const [mkt] = getMarket(place.lat, place.lng);
      let data;
      const apiKey = import.meta.env.VITE_ANTHROPIC_KEY;
      if (apiKey) {
        // Dev mode: call Anthropic directly
        const prompt = `You are estimating lawn measurements for a specific US residential property. Be conservative and realistic.

Address: ${place.address}
Market: ${mkt}

Return ONLY valid JSON. Do NOT guess high — it's better to underestimate than overestimate.
{
  "totalSqFt": "<total lot size in square feet — typical US suburban lot is 6000-15000>",
  "grassSqFt": "<mowable turf area ONLY — subtract house footprint, driveway, patios, beds. Usually 40-60% of total lot>",
  "houseSqFt": "<house footprint — typically 1200-2800 for suburban>",
  "treesCount": "<number of significant trees visible — be conservative, count only large trees>"
}

CRITICAL RULES:
- grassSqFt MUST be less than totalSqFt
- grassSqFt should be roughly (totalSqFt - houseSqFt) * 0.55
- treesCount should be conservative (most suburban yards have 2-6 trees)
- Do NOT inflate numbers`;
        const resp = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-api-key": apiKey, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
          signal: controller.signal,
          body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 500, messages: [{ role: "user", content: prompt }] }),
        });
        const raw = await resp.json();
        const text = raw.content?.find(b => b.type === "text")?.text || "{}";
        data = JSON.parse(text.replace(/```json|```/g, "").trim());
      } else {
        // Production: call through Worker proxy
        const resp = await fetch("/api/measure", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify({ address: place.address, market: mkt, lat: place.lat, lng: place.lng }),
        });
        data = await resp.json();
      }
      clearTimeout(timeout);
      // Validate: grass can't exceed total lot, apply sanity checks
      let total = data.totalSqFt || fallback.totalSqFt;
      let grass = data.grassSqFt || fallback.grassSqFt;
      let house = data.houseSqFt || fallback.houseSqFt;
      let trees = data.treesCount || fallback.treesCount;
      // If grass > total, Claude hallucinated — recalculate
      if (grass > total) { total = Math.round(grass * 1.5); }
      // Grass should be at most total minus house
      if (grass > total - house) { grass = Math.round((total - house) * 0.75); }
      // Minimum total lot
      if (total < 1000) total = fallback.totalSqFt;
      setPropertyData({ grassSqFt: grass, totalSqFt: total, houseSqFt: house, treesCount: trees });
    } catch {
      setPropertyData(fallback);
    }
    setMeasuring(false);
  };

  // Analyze
  const handleAnalyze = async () => {
    if (voice.active) voice.stop();
    await new Promise(r => setTimeout(r, 150));
    go("analyze");
    trackEvent('report_analyze');
    setDetects([]); setFlags([]); setStageIdx(0);
    const currentDesc = desc;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 45000);

    try {
      const content = [];
      const zonesWithPhotos = zones.filter(z => z.photos.length > 0);
      for (const zone of zonesWithPhotos) {
        for (const photo of zone.photos) {
          if (photo.file) {
            const b64 = await new Promise(res => { const r = new FileReader(); r.onload = e => res(e.target.result.split(",")[1]); r.readAsDataURL(photo.file); });
            content.push({ type: "image", source: { type: "base64", media_type: photo.file.type || "image/jpeg", data: b64 } });
            content.push({ type: "text", text: `[PHOTO ZONE: ${zone.label.toUpperCase()}] — Assess condition, identify species, note trimming needs, estimate height/spread.` });
          }
        }
      }

      await new Promise(r => setTimeout(r, 500)); setStageIdx(1);
      await new Promise(r => setTimeout(r, 400)); setStageIdx(2);

      const [mkt, mult] = getMarket(address.lat, address.lng);
      const availableSvcs = ALL_SVCS.filter(s => profile.servicesOffered.includes(s.id));
      const unavailableSvcs = ALL_SVCS.filter(s => !profile.servicesOffered.includes(s.id));
      const svcMenu = availableSvcs.map(s => `- ${s.id}: ${s.desc}`).join("\n");
      const referMenu = unavailableSvcs.length > 0 ? `\n\nSERVICES THIS CONTRACTOR DOES NOT OFFER (flag as "refer out" if detected):\n${unavailableSvcs.map(s => `- ${s.id}: ${s.desc}`).join("\n")}` : "";

      content.push({
        type: "text", text: `You are an expert lawn care estimator helping a professional landscaper price a job.

PROPERTY DATA:
- Address: ${address.address}
- Market: ${mkt} (${Math.round(mult * 100)}% of national avg)
- Turf area: ${propertyData.grassSqFt.toLocaleString()} sq ft
- Total lot: ${propertyData.totalSqFt.toLocaleString()} sq ft
- Trees on property: ~${propertyData.treesCount}

CONTRACTOR PROFILE:
- Team size: ${(profile.teamMembers || []).filter(m => m.active).length} member(s)
- Services they offer: ${profile.servicesOffered.join(", ")}

CUSTOMER DESCRIPTION (read every word carefully):
"${currentDesc || "No description provided"}"

SERVICES THIS CONTRACTOR OFFERS — pick only services explicitly mentioned or clearly implied:
${svcMenu}
${referMenu}

CONDITION RULES:
- norm: grass is maintained, normal height
- over: grass overgrown, 6-12 inches tall, needs extra passes
- negl: severely neglected, 12+ inches, abandoned, multiple visits may be needed

INSTRUCTIONS:
1. Read every word of the description. If they mention something, include the matching service.
2. Always include "maint" unless they explicitly said NO mowing.
3. Set condition based on grass height or description of overgrowth.
4. Create specific detections for each thing you found in the description and photos.
5. Flag anything that needs special pricing attention.
6. For services the contractor doesn't offer but the property needs, flag as "refer_out" type detection.
7. If any service might require a permit, flag it as "permit" type detection.

Return ONLY valid JSON, no other text:
{
  "condition": "norm" | "over" | "negl",
  "services": ["array of applicable service ids"],
  "detections": [
    {"label": "specific thing found", "tag": "short label", "type": "info" | "warn" | "refer" | "permit"}
  ],
  "flags": [
    {"label": "pricing note", "value": "quantity or impact", "icon": "warning"}
  ],
  "advice": "One specific pricing tactic for this exact job."
}`
      });

      await new Promise(r => setTimeout(r, 300)); setStageIdx(3);

      let data;
      const apiKey = import.meta.env.VITE_ANTHROPIC_KEY;
      if (apiKey) {
        // Dev mode: call Anthropic directly (no worker needed)
        const resp = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-api-key": apiKey, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
          signal: controller.signal,
          body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 2000, messages: [{ role: "user", content }] }),
        });
        data = await resp.json();
      } else {
        // Production: call through Worker proxy
        const resp = await fetch("/api/analyze", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify({ content, model: "claude-sonnet-4-20250514", max_tokens: 2000 }),
        });
        data = await resp.json();
      }
      if (data.error) throw new Error(data.error?.message || data.error);
      const rawText = data.content?.find(b => b.type === "text")?.text || "{}";
      const cleaned = rawText.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(cleaned);

      await new Promise(r => setTimeout(r, 300)); setStageIdx(4);
      for (let i = 0; i < (parsed.detections || []).length; i++) {
        await new Promise(r => setTimeout(r, 220));
        setDetects(p => [...p, parsed.detections[i]]);
      }
      await new Promise(r => setTimeout(r, 350));

      if (parsed.condition) { setCond(parsed.condition); setAiCond(parsed.condition); }
      if (parsed.services?.length) { setSvcs(parsed.services); setAiSvcs(parsed.services); }
      if (parsed.flags?.length) setFlags(parsed.flags);
      setUsedFallback(false);
      clearTimeout(timeout);
      go("review");
    } catch (err) {
      clearTimeout(timeout);
      console.info("AI analysis unavailable, using local estimation:", err.name === "AbortError" ? "timeout" : err.message);
      setUsedFallback(true);
      await new Promise(r => setTimeout(r, 400)); setStageIdx(1);
      await new Promise(r => setTimeout(r, 600)); setStageIdx(2);
      await new Promise(r => setTimeout(r, 500)); setStageIdx(3);
      await new Promise(r => setTimeout(r, 400)); setStageIdx(4);

      const fallback = parseScopeFallback(currentDesc);
      for (let i = 0; i < fallback.detections.length; i++) {
        await new Promise(r => setTimeout(r, 240));
        setDetects(p => [...p, fallback.detections[i]]);
      }
      await new Promise(r => setTimeout(r, 350));
      setCond(fallback.condition); setAiCond(fallback.condition);
      setSvcs(fallback.services); setAiSvcs(fallback.services);
      setFlags(fallback.flags);
      go("review");
    }
  };

  // Download PDF
  const handleDownload = async () => {
    trackEvent('report_download', { tier: selectedTier });
    if (!pdfReady) return;
    setGenerating(true);
    const msgs = ["Building report…", "Rendering property analysis…", "Exporting PDF…"];
    for (const m of msgs) { setLoadMsg(m); await new Promise(r => setTimeout(r, 500)); }
    try {
      const doc = await generatePDF({
        address: address?.address || "Property",
        mkt: pricing?.mkt || "Your Market",
        grassSqFt: propertyData?.grassSqFt || 4600,
        totalSqFt: propertyData?.totalSqFt || 8200,
        treesCount: propertyData?.treesCount || 3,
        condition: cond,
        pricing,
        scopeSlider,
        recurringSlider,
      }, biz || "Your Lawn Care Company", selectedTier);
      const fname = `MowRate_${selectedTier.toUpperCase()}_${(address?.address?.split(",")[0] || "Report").replace(/\s+/g, "_")}.pdf`;
      let saved = false;
      try { doc.save(fname); saved = true; } catch (dlErr) { console.warn("Download blocked by sandbox:", dlErr); saved = true; }

      // No credit deduction — subscription model gives unlimited access
      const selectedWeekly = Math.round((pricing.wLow + (pricing.wHigh - pricing.wLow) * (recurringSlider / 100)) / 5) * 5;
      const selectedScope = pricing.hasScopeWork ? Math.round((pricing.scopeLow + (pricing.scopeHigh - pricing.scopeLow) * (scopeSlider / 100)) / 5) * 5 : 0;
      const selectedBiweekly = Math.round(selectedWeekly * 1.15 / 5) * 5;
      setReportHistory(p => [{
        id: Date.now(),
        addr: address?.address?.split(",")[0] || "Property",
        fullAddress: address?.address || "Property",
        lat: address?.lat || null,
        lng: address?.lng || null,
        price: `$${selectedWeekly}`,
        date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        fullDate: new Date().toISOString(),
        sqft: propertyData?.grassSqFt,
        tier: selectedTier,
        propertyData: { ...propertyData },
        condition: cond,
        services: [...svcs],
        pricing: { ...pricing },
        selectedWeekly, selectedBiweekly, selectedScope,
        scopeSlider, recurringSlider,
        clientId: selectedClientForReport?.id || null,
        laborBreakdown: pricing?.laborBreakdown || [],
        overheadPerJob: pricing?.overheadPerJob || 0,
        profileSnapshot: {
          teamMembers: (profile.teamMembers || []).filter(m => m.active).map(m => ({ ...m })),
          expenses: profile.expenses.map(e => ({ ...e })),
          customExpenses: (profile.customExpenses || []).map(e => ({ ...e })),
          estimatedMonthlyJobs: profile.estimatedMonthlyJobs,
        },
      }, ...p.slice(0, 19)]);
      setGenerating(false);
      go("dashboard");
    } catch (e) {
      console.error(e); setGenerating(false);
    }
  };

  const handleRequote = () => {
    if (!selectedReport) return;
    const r = selectedReport;
    setAddress({ address: r.fullAddress || r.addr, lat: r.lat || null, lng: r.lng || null });
    setPropertyData(r.propertyData ? { ...r.propertyData } : null);
    setMeasuring(false);
    setZones(DEFAULT_ZONES.map(z => ({ ...z, photos: [] })));
    setDesc(""); setSvcs(["maint"]); setCond("norm");
    setAiSvcs([]); setAiCond(null); setFlags([]); setDetects([]);
    setShowMore(false); setPricing(null);
    setSelectedTier("pro");
    setSelectedClientForReport(null);
    go("step2");
  };

  const reset = () => {
    setAddress(null); setPropertyData(null); setMeasuring(false);
    setZones(DEFAULT_ZONES.map(z => ({ ...z, photos: [] })));
    setNewZoneLabel(""); setDesc("");
    setSvcs(["maint"]); setCond("norm");
    setAiSvcs([]); setAiCond(null); setFlags([]); setDetects([]);
    setShowMore(false); setPricing(null);
    setSelectedTier("pro");
    setSelectedClientForReport(null);
    go("step1");
  };

  // Subscription check replaces old credit count
  const [mktName, mktMult] = address ? getMarket(address.lat, address.lng) : ["—", 1];
  const sc = `screen ${dir === "back" ? "screen-back" : ""}`;

  return (
    <>
      <div className="amb" />
      <div className="app">
        <Header screen={screen} go={go} subscription={subscription} />
        <Suspense fallback={<ScreenLoader />}>

        {screen === "signup" && <SignupScreen sc={sc} signStep={signStep} setSignStep={setSignStep} email={email} setEmail={setEmail} biz={biz} setBiz={setBiz} phone={phone} setPhone={setPhone} go={go} infoOk={infoOk} />}

        {screen === "onboarding" && <OnboardingScreen sc={sc} profile={profile} setProfile={setProfile} go={go} setBiz={setBiz} biz={biz} />}

        {screen === "dashboard" && <DashboardScreen sc={sc} biz={biz} hasAccess={hasAccess} subscription={subscription} reportHistory={reportHistory} go={go} reset={reset} setSelectedReport={setSelectedReport} />}

        {screen === "step1" && <Step1Screen sc={sc} address={address} propertyData={propertyData} measuring={measuring} mktName={mktName} mktMult={mktMult} step1Ok={step1Ok} go={go} handleAddressSelect={handleAddressSelect} onUpdateGrassSqFt={(sqft) => setPropertyData(prev => prev ? { ...prev, grassSqFt: sqft } : prev)} onUpdatePropertyData={(updates) => setPropertyData(prev => prev ? { ...prev, ...updates } : prev)} />}

        {screen === "step2" && <Step2Screen sc={sc} propertyData={propertyData} address={address} zones={zones} desc={desc} setDesc={setDesc} voice={voice} step2Ok={step2Ok} go={go} back={back} handleAnalyze={handleAnalyze} addPhotoToZone={addPhotoToZone} removePhotoFromZone={removePhotoFromZone} addCustomZone={addCustomZone} newZoneLabel={newZoneLabel} setNewZoneLabel={setNewZoneLabel} />}

        {screen === "analyze" && <AnalyzeScreen sc={sc} stageIdx={stageIdx} detects={detects} propertyData={propertyData} address={address} />}

        {screen === "review" && <ReviewScreen sc={sc} pricing={pricing} scopeSlider={scopeSlider} setScopeSlider={setScopeSlider} recurringSlider={recurringSlider} setRecurringSlider={setRecurringSlider} cond={cond} setCond={setCond} aiCond={aiCond} svcs={svcs} setSvcs={setSvcs} aiSvcs={aiSvcs} flags={flags} showMore={showMore} setShowMore={setShowMore} profile={profile} setProfile={setProfile} editingPrice={editingPrice} setEditingPrice={setEditingPrice} setEditPriceValue={setEditPriceValue} go={go} back={back} usedFallback={usedFallback} />}

        {screen === "tierselect" && <TierSelectScreen sc={sc} selectedTier={selectedTier} setSelectedTier={setSelectedTier} subscription={subscription} go={go} />}

        {screen === "attachclient" && <AttachClientScreen sc={sc} clients={clients} selectedClientForReport={selectedClientForReport} setSelectedClientForReport={setSelectedClientForReport} go={go} addClient={addClient} />}

        {screen === "preview" && <PreviewScreen sc={sc} selectedTier={selectedTier} biz={biz} address={address} propertyData={propertyData} pricing={pricing} cond={cond} hasAccess={hasAccess} logo={logo} logoUnlocked={logoUnlocked} pdfReady={pdfReady} pdfError={pdfError} pdfRetry={pdfRetry} generating={generating} loadMsg={loadMsg} handleDownload={handleDownload} go={go} back={back} />}

        {screen === "pricing" && <PricingScreen sc={sc} profile={profile} setProfile={setProfile} />}

        {screen === "costs" && <CostsScreen sc={sc} profile={profile} setProfile={setProfile} />}

        {screen === "profile" && <ProfileScreen sc={sc} biz={biz} setBiz={setBiz} email={email} setEmail={setEmail} phone={phone} setPhone={setPhone} profile={profile} setProfile={setProfile} logo={logo} setLogo={setLogo} setLogoFile={setLogoFile} logoUnlocked={logoUnlocked} setLogoUnlocked={setLogoUnlocked} go={go} />}

        {screen === "settings" && <SettingsScreen sc={sc} subscription={subscription} cardNum="" logoUnlocked={logoUnlocked} go={go} setScreen={setScreen} setSignStep={setSignStep} />}

        {screen === "buy" && <BuyScreen sc={sc} subscription={subscription} back={back} />}

        {screen === "clients" && <ClientsScreen sc={sc} clients={clients} filteredClients={filteredClients} clientSearch={clientSearch} setClientSearch={setClientSearch} reportHistory={reportHistory} go={go} setSelectedClient={setSelectedClient} setEditingClient={setEditingClient} setAttachClientTo={setAttachClientTo} getClientStatus={getClientStatus} />}

        {screen === "clientdetail" && selectedClient && <ClientDetailScreen sc={sc} selectedClient={selectedClient} reportHistory={reportHistory} go={go} setSelectedReport={setSelectedReport} setEditingClient={setEditingClient} updateClient={updateClient} setSelectedClient={setSelectedClient} reset={reset} getClientStatus={getClientStatus} />}

        {screen === "reportdetail" && selectedReport && <ReportDetailScreen sc={sc} selectedReport={selectedReport} biz={biz} pdfReady={pdfReady} profile={profile} reportHistory={reportHistory} setReportHistory={setReportHistory} setSelectedReport={setSelectedReport} go={go} onRequote={handleRequote} />}
        </Suspense>
      </div>

      <BottomTabs screen={screen} go={go} />

      {editingPrice && <PriceEditModal editingPrice={editingPrice} setEditingPrice={setEditingPrice} editPriceValue={editPriceValue} setEditPriceValue={setEditPriceValue} profile={profile} setProfile={setProfile} />}

      {editingClient && <ClientEditModal editingClient={editingClient} setEditingClient={setEditingClient} addClient={addClient} updateClient={updateClient} setSelectedClient={setSelectedClient} attachClientTo={attachClientTo} setAttachClientTo={setAttachClientTo} attachReportToClient={attachReportToClient} />}

      {attachClientTo && !editingClient && <AttachClientModal attachClientTo={attachClientTo} setAttachClientTo={setAttachClientTo} clients={clients} reportHistory={reportHistory} attachReportToClient={attachReportToClient} setEditingClient={setEditingClient} />}
    </>
  );
}
